import { Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ClearAccountCache, GetAccount } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import { ClearNotificationsCache } from '@app/finxone-web-frontend/app/lib/actions/notification.action';
import { GetOrganisation } from '@app/finxone-web-frontend/app/lib/actions/organisation.action';
import { UpdateRole } from '@app/finxone-web-frontend/app/lib/actions/role.action';
import {
  GetProfile,
  SetActiveOrgId,
  SetActiveRole,
} from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { KeycloakCustomService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-custom.service';
import { UserProfileType } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-custom.type';
import { KeycloakWrapperService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import { NotificationService } from '@app/finxone-web-frontend/app/lib/services/notification-service/notification-service.service';
import { SocketService } from '@app/finxone-web-frontend/app/lib/services/socket-service/socket-service.service';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { OrganisationSuccessCodes } from '@app/translations';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  LayoutWidgetConfig,
  OrganisationList,
  ProfileStateModel,
  ProviderClass,
  TemplateType,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, combineLatest, map, take, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { BaseComponent } from '../base/base.component';

interface OrganizationDetails {
  attributes: {
    externalClientId: string;
  };
}

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-multi-role-widget',
  templateUrl: './multi-role-widget.component.html',
  styleUrls: ['./multi-role-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MultiRoleWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public zone = 'profile';
  public widgetConfig: UiZoneWidgetConfig;
  multiRoleForm: FormGroup;
  isError = false;
  widgetsAttributes: LayoutWidgetConfig[] = [];
  nameString = '';
  userProfileData = '';
  urlParams: string[] = [];
  isShowErrorMsg = false;
  currentWidgetPosition: number;
  colorTextAlignStyle: baseCssConfigStyle;
  colorStyle: baseCssConfigStyle;
  borderStyle: baseCssConfigStyle;
  selectedOrg = '';
  selectedRole = '';
  organisationList: any;
  public availablePersonasForOrg: any;
  profileStateModelData: ProfileStateModel;

  @Select(ProfileState.getProfile)
  profileStateData!: Observable<ProfileStateModel>;

  public inputStyle: baseCssConfigStyle;
  public inputBaseStyle: baseCssConfigStyle;
  public inputInactiveStyle: baseCssConfigStyle;
  public currentInputStyle: baseCssConfigStyle;
  public showRolePreview = true;
  public showPersonaDropdown = false;
  public organisationLabel = 'Organisation';
  public templateMode = TemplateType;
  public idleStateTemplate: string;
  public organisationListData: UserProfileType[] = [];
  public activeOrganisationId: string | undefined;
  public selectedStateTemplate: string;
  public templateType: TemplateType;
  public baseCssStyle: string;
  public noRecordsTemplate = 'No organisation found.';
  public gridHeight: number;
  public organizationDetails: UserProfileType[] = [];
  private observer: MutationObserver;
  public selectedPersonaPreview = 'Permission - Persona - {{ profile.activeRole }}';
  public selectedOrganisation: { key: string; title: string };
  public availablePersonas: string[];

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<any>,
    private store: Store,
    private accountService: AccountService,
    private metadataService: MetadataService,
    private keycloakService: KeycloakWrapperService,
    private keycloakCustomService: KeycloakCustomService,
    private alertHandlerService: AlertHandlerService,
    private notificationService: NotificationService,
    private elementRef: ElementRef,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private socketService: SocketService,
  ) {
    super();
  }

  ngOnInit(): void {
    const profileId = this.store.selectSnapshot(ProfileState.getProfile)?.id ?? '';
    this.getOrganisationData(profileId);
    this.getWidgetData();
  }

  async getOrganisationData(id: string) {
    const userOrganisationList = await this.keycloakCustomService.getUserOrganisationList(id);
    if (userOrganisationList) this.organisationListData = userOrganisationList;
    combineLatest([this.profileStateData, this.configService.getRoles()])
      .pipe(
        take(1),
        map(([profileData, roles]) => {
          this.availablePersonas = Object.keys(roles);
          this.organizationDetails = [];
          this.organisationList = profileData.orgList
            ?.filter((item) => item.roles.length > 0)
            ?.map((item) => {
              const user = this.organisationListData?.find((profile) => profile?.id === item?.id);
              if (user && item?.roles?.length > 0) {
                this.organizationDetails?.push(user);
                return {
                  key: user?.id ?? item.id,
                  title: user?.displayName ?? item.name,
                };
              } else {
                return null;
              }
            });

          this.selectedOrg = profileData.activeOrganisationId ?? '';

          this.getSelectedOrganisation();

          this.availablePersonasForOrg = profileData.orgList
            ?.filter((org) => org.id === this.selectedOrg)
            .flatMap((item) => this.mapRolesToPersonas(item.roles));

          this.selectedRole = profileData.activeRole ?? '';

          this.profileStateModelData = profileData;
        }),
      )
      .subscribe();
  }

  private getSelectedOrganisation() {
    this.selectedOrganisation = this.organisationList.find(
      (obj: { key: string | undefined }) => obj.key === this.selectedOrg,
    );
  }

  getWidgetData() {
    if (this.dataIn != undefined && this.dataIn != null) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.userProfileData = this.dataIn.userProfileData;
      this.attri = this.dataIn.attri;
      this.showRolePreview = this.attri?.['showRolePreview'] ?? this.showRolePreview;
      this.showPersonaDropdown = this.attri?.['showPersonaDropdown'] ?? this.showPersonaDropdown;
      this.organisationLabel = this.attri?.['organisationLabel'] ?? this.organisationLabel;
      this.idleStateTemplate = this.attri?.['idleStateTemplate'] ?? '';
      this.selectedStateTemplate = this.attri?.['selectedStateTemplate'] ?? '';
      this.noRecordsTemplate = this.attri?.['noRecordsTemplate'] ?? this.noRecordsTemplate;
      this.baseCssStyle = this.attri?.['baseCssStyle'] ?? '';
      this.templateType = this.attri?.['templateType'] ?? TemplateType.DROPDOWN;
      this.getSelectedPersonaTemplate();
    }
    this.buildForm();
    this.activeOrganisationId = this.store.selectSnapshot(ProfileState.getProfile).activeOrganisationId;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
    ]);

    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXTALIGN,
    ]);

    this.colorStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.COLOR]);

    this.borderStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.BORDER]);
  }

  private getSelectedPersonaTemplate() {
    this.selectedPersonaPreview =
      this.attri?.['selectedPersonaPreview'] ?? 'Permission - Persona - {{ profile.activeRole }}';
    const template = Handlebars.compile(this.selectedPersonaPreview);
    buildHandleBarModel(
      this.store,
      this.selectedPersonaPreview,
      this.countriesCurrenciesService,
      this.iconCacheService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.selectedPersonaPreview = filterIfHandlebarNotRendered(template(model));
      });
  }

  buildForm() {
    const formGroup = new FormGroup({});
    for (const field of this.widgetConfig.attributes['field_attributes']) {
      formGroup.addControl(field.name, new FormControl('', []));
    }

    this.multiRoleForm = formGroup;
  }

  onSubmit() {
    if (this.multiRoleForm.valid) {
      const nameObject = this.multiRoleForm.value;
      let name = '';
      Object.values(nameObject).forEach((value) => {
        name = name + value + ' ';
      });
      this.nameString = name;
      this.isError = false;
    } else {
      this.multiRoleForm.markAllAsTouched();
      this.isError = true;
    }
  }

  showErrorMsg() {
    this.isShowErrorMsg = !this.isShowErrorMsg;
  }

  private updateRoleAndOrganization(): void {
    const newOrgId = this.multiRoleForm.value.organisation;

    // Switch socket rooms to new organization
    if (this.selectedOrg !== newOrgId) {
      this.socketService.switchOrganization(newOrgId);
    }

    this.selectedOrg = newOrgId;
    this.availablePersonasForOrg = this.getRolesForOrganization(this.selectedOrg);
    this.updateSelectedRole();
    this.dispatchStoreActions();
    const userId = this.store.selectSnapshot(ProfileState.getProfile)?.id;
    if (userId) {
      localStorage.setItem(`lastSelectedOrg`, userId + ':' + this.selectedOrg);
    }
  }

  private getRolesForOrganization(orgId: string): { key: string; title: string }[] {
    if (!this.profileStateModelData?.orgList) return [];

    return this.profileStateModelData.orgList
      .filter((org) => org.id === orgId)
      .flatMap((org) => this.mapRolesToPersonas(org.roles));
  }

  private mapRolesToPersonas(roles: string[]): { key: string; title: string }[] {
    if (!roles || !this.availablePersonas) return [];
    return roles
      .filter((role) => this.availablePersonas.includes(role))
      .map((role) => ({ key: role, title: role }));
  }

  private updateSelectedRole(): void {
    this.selectedRole = this.profileStateModelData.activeRole ?? '';
    this.multiRoleForm.value.role = this.selectedRole;
  }

  private dispatchStoreActions(): void {
    this.store.dispatch(new SetActiveOrgId(this.selectedOrg));
    // SetActiveRole before UpdateRole: collectRouteParams reverts RoleState if it leads ProfileState.
    // UpdateRole needed here since setRoleInfo's leaked subscription (removed) no longer syncs RoleState on org switch.
    this.store.dispatch(new SetActiveRole(this.availablePersonasForOrg[0]?.key));
    this.store.dispatch(new UpdateRole(this.availablePersonasForOrg[0]?.key));
    this.store.dispatch(new ClearAccountCache());
    this.store.dispatch(new GetAccount());

    this.store.dispatch(new GetOrganisation(this.selectedOrg));
    this.store.dispatch(new GetProfile());
    this.store.dispatch(new ClearNotificationsCache());
    this.notificationService.setupNotifications();
  }

  private handleDeviceToken(): void {
    const deviceToken = localStorage.getItem('deviceToken');
    if (!deviceToken) return;

    this.accountService
      .updateDeviceTokenOnOrgChanged({ tokens: [deviceToken] })
      .pipe(take(1))
      .subscribe();
  }

  public organisationChange(): void {
    this.updateRoleAndOrganization();

    this.handleDeviceToken();
  }

  roleChange(role: string) {
    // SetActiveRole must land before UpdateRole: config-service collectRouteParams
    // resets RoleState back to profile.activeRole whenever the two disagree
    this.store.dispatch(new SetActiveRole(role));
    this.store.dispatch(new UpdateRole(role));
    this.store.dispatch(new ClearAccountCache());
    this.store.dispatch(new GetAccount());
  }

  public updateSelectedOrganisation(org: OrganisationList) {
    this.activeOrganisationId = org.id;
    this.alertHandlerService.resetAlertFn();
    this.selectedOrg = org.id;
    this.availablePersonasForOrg = this.getRolesForOrganization(this.selectedOrg);
    this.updateSelectedRole();
    this.dispatchStoreActions();
    findAndShowAlertFromCode(this.store, this.parentRouter, this.alertHandlerService, [
      OrganisationSuccessCodes.ORGANISATION_SWITCH_SUCCESSFUL,
    ]);
    this.handleDeviceToken();
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
